import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronRightIcon, StarIcon, TrendingUpIcon, InfoIcon, CheckIcon, CalendarIcon, BookmarkIcon, ArrowRightIcon, UserIcon, ClockIcon, SendIcon, MessageCircleIcon, XIcon, EyeIcon, TrophyIcon, AwardIcon, TargetIcon, UsersIcon } from 'lucide-react';
import FirstLoginAlert from '../../components/FirstLoginAlert';
import FirstLoginModal from '../../components/FirstLoginModal';
import BuddyRequestAlert from '../../components/BuddyRequestAlert';
interface Trophy {
  id: number;
  emoji?: string;
  title: string;
  description?: string;
  points: number;
  date?: string;
  visible: boolean;
}
interface Goal {
  id: number;
  title: string;
  progress: number;
  status: string;
  category?: string;
  personal?: string;
  relational?: string;
  intentional?: string;
  dynamic?: string;
  empowered?: string;
}
const GoalCard = ({
  goal
}: {
  goal: Goal;
}) => {
  return <div className="bg-white rounded-xl shadow-sm p-3" data-id="element-827">
      <div className="flex justify-between items-start mb-2" data-id="element-828">
        <h3 className="font-medium" data-id="element-829">{goal.title}</h3>
      </div>
      {goal.status === 'inProgress' && <>
          <div className="w-full bg-gray-200 rounded-full h-2 mb-1" data-id="element-830">
            <div className="bg-purple-600 h-2 rounded-full" style={{
          width: `${goal.progress}%`
        }} data-id="element-831"></div>
          </div>
          <div className="flex justify-between text-xs text-gray-500" data-id="element-832">
            <span data-id="element-833">{goal.progress}% complete</span>
          </div>
        </>}
      {goal.status === 'todo' && <div className="flex items-center text-xs text-gray-500" data-id="element-834">
          <ClockIcon size={12} className="mr-1" data-id="element-835" /> Not started
        </div>}
      {goal.status === 'completed' && <div className="flex items-center text-xs text-green-600" data-id="element-836">
          <CheckIcon size={12} className="mr-1" data-id="element-837" /> Completed
        </div>}
    </div>;
};
const TrophyPreviewModal = ({
  trophy,
  onClose
}: {
  trophy: Trophy | null;
  onClose: () => void;
}) => {
  if (!trophy) return null;
  return <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" data-id="element-838">
      <div className="bg-white rounded-xl w-full max-w-md" data-id="element-839">
        <div className="p-4 border-b border-gray-200 flex justify-between items-center" data-id="element-840">
          <h3 className="font-semibold" data-id="element-841">Trophy Details</h3>
          <button onClick={onClose} data-id="element-842">
            <XIcon size={20} data-id="element-843" />
          </button>
        </div>
        <div className="p-6" data-id="element-844">
          <div className="flex flex-col items-center mb-4" data-id="element-845">
            <div className="text-4xl mb-3" data-id="element-846">
              {trophy.title === 'First Goal Set' && <AwardIcon size={48} className="text-purple-600" data-id="element-847" />}
              {trophy.title === 'Goal Achiever' && <TargetIcon size={48} className="text-purple-600" data-id="element-848" />}
              {trophy.title === 'Meeting Master' && <CalendarIcon size={48} className="text-purple-600" data-id="element-849" />}
              {trophy.title === 'Rising Star' && <StarIcon size={48} className="text-purple-600" data-id="element-850" />}
              {trophy.title === 'Knowledge Seeker' && <BookmarkIcon size={48} className="text-purple-600" data-id="element-851" />}
              {trophy.title === 'Community Builder' && <UsersIcon size={48} className="text-purple-600" data-id="element-852" />}
              {trophy.title === 'Engagement Pro' && <MessageCircleIcon size={48} className="text-purple-600" data-id="element-853" />}
              {trophy.title === 'Feedback Champion' && <TrendingUpIcon size={48} className="text-purple-600" data-id="element-854" />}
            </div>
            <h2 className="text-xl font-bold" data-id="element-855">{trophy.title}</h2>
            <p className="text-purple-600 font-medium" data-id="element-856">
              {trophy.points} points
            </p>
          </div>
          {trophy.description && <p className="text-gray-600 text-center mb-4" data-id="element-857">
              {trophy.description}
            </p>}
          {trophy.date && <p className="text-sm text-gray-500 text-center" data-id="element-858">
              Earned on {trophy.date}
            </p>}
        </div>
      </div>
    </div>;
};
const ResourcePreviewModal = ({
  resource,
  onClose
}: {
  resource: any;
  onClose: () => void;
}) => {
  if (!resource) return null;
  return <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" data-id="element-859">
      <div className="bg-white rounded-xl w-full max-w-md max-h-[90vh] overflow-y-auto" data-id="element-860">
        <div className="p-4 border-b border-gray-200 flex justify-between items-center" data-id="element-861">
          <h3 className="font-semibold" data-id="element-862">Resource Preview</h3>
          <button onClick={onClose} data-id="element-863">
            <XIcon size={20} data-id="element-864" />
          </button>
        </div>
        <div className="p-4" data-id="element-865">
          <h2 className="text-lg font-semibold mb-2" data-id="element-866">{resource.title}</h2>
          <p className="text-gray-600 mb-4" data-id="element-867">{resource.description}</p>
          <div className="flex items-center justify-between" data-id="element-868">
            <span className="text-sm bg-purple-100 text-purple-600 px-2 py-0.5 rounded-full" data-id="element-869">
              Resource
            </span>
            <button className="text-purple-600 hover:text-purple-700" data-id="element-870">
              <BookmarkIcon size={16} data-id="element-871" />
            </button>
          </div>
        </div>
      </div>
    </div>;
};
const ExtendMentorshipModal = ({
  onClose
}: {
  onClose: () => void;
}) => {
  return <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" data-id="element-872">
      <div className="bg-white rounded-xl w-full max-w-md" data-id="element-873">
        <div className="p-4 border-b border-gray-200 flex justify-between items-center" data-id="element-874">
          <h3 className="font-semibold" data-id="element-875">Extend Mentorship</h3>
          <button onClick={onClose} data-id="element-876">
            <XIcon size={20} data-id="element-877" />
          </button>
        </div>
        <div className="p-6" data-id="element-878">
          <p className="text-gray-600 mb-4" data-id="element-879">
            Would you like to extend your mentorship for another period? This
            will help you continue your growth journey.
          </p>
          <div className="space-y-3 mb-6" data-id="element-880">
            <button className="w-full p-3 border border-gray-300 rounded-lg text-left hover:border-purple-300 hover:bg-purple-50" data-id="element-881">
              <div className="font-medium" data-id="element-882">3 more months</div>
              <div className="text-sm text-gray-500" data-id="element-883">Recommended option</div>
            </button>
            <button className="w-full p-3 border border-gray-300 rounded-lg text-left hover:border-purple-300 hover:bg-purple-50" data-id="element-884">
              <div className="font-medium" data-id="element-885">6 more months</div>
              <div className="text-sm text-gray-500" data-id="element-886">Best value</div>
            </button>
            <button className="w-full p-3 border border-gray-300 rounded-lg text-left hover:border-purple-300 hover:bg-purple-50" data-id="element-887">
              <div className="font-medium" data-id="element-888">Custom duration</div>
            </button>
          </div>
          <div className="flex justify-end space-x-3" data-id="element-889">
            <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg" data-id="element-890">
              Cancel
            </button>
            <button className="px-4 py-2 bg-purple-600 text-white rounded-lg" onClick={onClose} data-id="element-891">
              Confirm Extension
            </button>
          </div>
        </div>
      </div>
    </div>;
};
const GoalDetailModal = ({
  goal,
  onClose
}: {
  goal: any;
  onClose: () => void;
}) => {
  if (!goal) return null;
  return <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" data-id="element-892">
      <div className="bg-white rounded-xl w-full max-w-md" data-id="element-893">
        <div className="p-4 border-b border-gray-200 flex justify-between items-center" data-id="element-894">
          <h3 className="font-semibold" data-id="element-895">Goal Details</h3>
          <button onClick={onClose} data-id="element-896">
            <XIcon size={20} data-id="element-897" />
          </button>
        </div>
        <div className="p-6" data-id="element-898">
          <h2 className="text-lg font-semibold mb-3" data-id="element-899">{goal.title}</h2>
          <div className="mb-4" data-id="element-900">
            <div className="flex justify-between items-center mb-1" data-id="element-901">
              <span className="text-sm text-gray-600" data-id="element-902">Progress</span>
              <span className="text-sm font-medium" data-id="element-903">{goal.progress}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2" data-id="element-904">
              <div className="bg-purple-600 h-2 rounded-full" style={{
              width: `${goal.progress}%`
            }} data-id="element-905"></div>
            </div>
          </div>
          <div className="flex justify-end" data-id="element-906">
            <button onClick={onClose} className="px-4 py-2 bg-purple-600 text-white rounded-lg" data-id="element-907">
              Close
            </button>
          </div>
        </div>
      </div>
    </div>;
};
const hideTrophy = (trophyId: number) => {
  console.log(`Hiding trophy with id ${trophyId}`);
};
const MenteeDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [showFirstLoginAlert, setShowFirstLoginAlert] = useState(false);
  const [showFirstLoginModal, setShowFirstLoginModal] = useState(false);
  const [showTrophyDetails, setShowTrophyDetails] = useState<number | null>(null);
  const [showGoalDetailModal, setShowGoalDetailModal] = useState<any>(null);
  const [showResourcePreview, setShowResourcePreview] = useState<number | null>(null);
  const [showExtendModal, setShowExtendModal] = useState(false);
  const [showBuddyAlert, setShowBuddyAlert] = useState(() => {
    const dismissed = localStorage.getItem('buddyAlertDismissed');
    const hasBuddies = localStorage.getItem('supportBuddies');
    return !dismissed && !hasBuddies;
  });
  const handleBuddyAlertDismiss = () => {
    setShowBuddyAlert(false);
    localStorage.setItem('buddyAlertDismissed', 'true');
  };
  const handleAddBuddy = () => {
    navigate('/settings');
  };
  const alerts = [showBuddyAlert && <BuddyRequestAlert key="buddy-alert" onDismiss={handleBuddyAlertDismiss} onAddBuddy={handleAddBuddy} data-id="element-908" />, showFirstLoginAlert && <FirstLoginAlert key="first-login-alert" onDismiss={() => setShowFirstLoginAlert(false)} data-id="element-909" />, showFirstLoginModal && <FirstLoginModal key="first-login-modal" onDismiss={() => setShowFirstLoginModal(false)} data-id="element-910" />].filter(Boolean);
  // Add mock data
  const dashboardData = {
    mentee: {
      name: 'Alex Kim',
      image: 'https://randomuser.me/api/portraits/nonbinary/12.jpg'
    },
    mentor: {
      name: 'Dr. Sarah Chen',
      role: 'Mental Health Counselor',
      image: 'https://randomuser.me/api/portraits/women/44.jpg'
    },
    nextMeeting: {
      date: 'Tomorrow',
      time: '3:00 PM EST'
    },
    recommendedResources: [{
      id: 1,
      title: 'Managing Anxiety Guide',
      description: 'Learn effective techniques for managing anxiety and stress.'
    }, {
      id: 2,
      title: 'Self-Care Strategies',
      description: 'Practical approaches to maintaining mental well-being.'
    }]
  };
  const goals = [{
    id: 1,
    title: 'Develop Coping Strategies',
    category: 'Mental Health',
    status: 'inProgress',
    progress: 45,
    personal: 'I need better stress management techniques',
    relational: 'Will help me be more present with others',
    intentional: 'Mental health is a priority for overall wellbeing',
    dynamic: 'Adjusting techniques as I learn what works',
    empowered: 'I feel calmer when I have strategies in place'
  }, {
    id: 2,
    title: 'Build Support Network',
    category: 'Community',
    status: 'inProgress',
    progress: 30,
    personal: 'I want to expand my support system',
    relational: 'Building meaningful connections',
    intentional: 'Strong support network is essential',
    dynamic: 'Growing network gradually',
    empowered: 'Each connection makes me feel stronger'
  }, {
    id: 3,
    title: 'Research grad school programs',
    category: 'Academic',
    status: 'todo',
    progress: 0,
    personal: 'I want to advance my education in psychology',
    relational: 'Will connect me with academic mentors and peers',
    intentional: 'Further education aligns with my career goals',
    dynamic: 'Identify 5 programs that fit my interests and apply to at least 3',
    empowered: 'Taking this step makes me feel in control of my future'
  }];
  const trophies = [{
    id: 1,
    title: 'First Goal Set',
    points: 50,
    visible: true
  }, {
    id: 2,
    title: 'Goal Achiever',
    points: 100,
    visible: true
  }, {
    id: 3,
    title: 'Meeting Master',
    points: 75,
    visible: true
  }, {
    id: 4,
    title: 'Rising Star',
    points: 150,
    visible: true
  }, {
    id: 5,
    title: 'Knowledge Seeker',
    points: 100,
    visible: true
  }, {
    id: 6,
    title: 'Community Builder',
    points: 200,
    visible: true
  }, {
    id: 7,
    title: 'Engagement Pro',
    points: 125,
    visible: true
  }, {
    id: 8,
    title: 'Feedback Champion',
    points: 175,
    visible: true
  }];
  const todayQuote = {
    text: 'Hope will never be silent.',
    author: 'Harvey Milk'
  };
  return <div className="space-y-6 pb-4" data-id="element-911">
      <div className="bg-white -mx-4 px-4 pt-6 pb-6 mb-6" data-id="element-912">
        <div className="text-center mb-6" data-id="element-913">
          <h1 className="text-2xl font-bold mb-2" data-id="element-914">
            Welcome back, {dashboardData?.mentee?.name || 'User'}!
          </h1>
          <div className="w-full overflow-hidden" data-id="element-915">
            <div className="animate-scroll-left hover:pause" data-id="element-916">
              <span className="inline-block text-gray-600 italic" data-id="element-917">
                "{todayQuote.text}" – {todayQuote.author}
              </span>
            </div>
          </div>
        </div>
        <div className="flex gap-3 overflow-x-auto pb-2 hide-scrollbar" data-id="element-918">
          {trophies.filter(t => t.visible).map(trophy => <div key={trophy.id} className="flex-shrink-0" data-id="element-919">
                <div className="bg-purple-50 rounded-xl p-4 cursor-pointer relative group flex flex-col items-center text-center min-w-[160px]" onClick={() => setShowTrophyDetails(trophy.id)} data-id="element-920">
                  <button onClick={e => {
              e.stopPropagation();
              hideTrophy(trophy.id);
            }} className="absolute -top-2 -right-2 bg-white rounded-full p-1 shadow-sm opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-921">
                    <XIcon size={14} className="text-gray-400" data-id="element-922" />
                  </button>
                  <div className="mb-3 text-purple-700" data-id="element-923">
                    {trophy.title === 'First Goal Set' && <AwardIcon size={32} data-id="element-924" />}
                    {trophy.title === 'Goal Achiever' && <TargetIcon size={32} data-id="element-925" />}
                    {trophy.title === 'Meeting Master' && <CalendarIcon size={32} data-id="element-926" />}
                    {trophy.title === 'Rising Star' && <StarIcon size={32} data-id="element-927" />}
                    {trophy.title === 'Knowledge Seeker' && <BookmarkIcon size={32} data-id="element-928" />}
                    {trophy.title === 'Community Builder' && <UsersIcon size={32} data-id="element-929" />}
                    {trophy.title === 'Engagement Pro' && <MessageCircleIcon size={32} data-id="element-930" />}
                    {trophy.title === 'Feedback Champion' && <TrendingUpIcon size={32} data-id="element-931" />}
                  </div>
                  <div className="font-medium mb-1" data-id="element-932">{trophy.title}</div>
                  <div className="text-sm text-purple-600" data-id="element-933">
                    +{trophy.points} pts
                  </div>
                </div>
              </div>)}
        </div>
      </div>
      <div className="bg-white rounded-xl shadow-sm p-6" data-id="element-934">
        <h2 className="text-lg font-semibold mb-4" data-id="element-935">Your Mentor</h2>
        <div className="flex items-center justify-between mb-4" data-id="element-936">
          <div className="flex items-center" data-id="element-937">
            <img src={dashboardData.mentor.image} alt={dashboardData.mentor.name} className="w-12 h-12 rounded-full object-cover" data-id="element-938" />
            <div className="ml-3" data-id="element-939">
              <p className="font-medium" data-id="element-940">{dashboardData.mentor.name}</p>
              <p className="text-sm text-gray-500" data-id="element-941">
                {dashboardData.mentor.role}
              </p>
            </div>
          </div>
          <button className="cta-icon text-purple-600" onClick={() => navigate('/messages')} data-id="element-942">
            <MessageCircleIcon size={20} data-id="element-943" />
          </button>
        </div>
        <div className="bg-purple-50 rounded-lg p-4" data-id="element-944">
          <div className="flex justify-between items-start" data-id="element-945">
            <div data-id="element-946">
              <div className="text-sm text-purple-600 mb-1" data-id="element-947">Next Session</div>
              <div className="font-semibold" data-id="element-948">
                {dashboardData.nextMeeting.date}
              </div>
              <div className="text-sm text-gray-600" data-id="element-949">
                {dashboardData.nextMeeting.time}
              </div>
            </div>
            <ClockIcon className="text-purple-600" size={20} data-id="element-950" />
          </div>
          <div className="flex space-x-2 mt-4" data-id="element-951">
            <button className="flex-1 px-3 py-2 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700" data-id="element-952">
              Join Meeting
            </button>
            <button className="flex-1 px-3 py-2 border border-purple-600 text-purple-600 rounded-lg text-sm hover:bg-purple-50" data-id="element-953">
              Reschedule
            </button>
          </div>
        </div>
      </div>
      <div className="bg-white rounded-xl shadow-sm p-6" data-id="element-954">
        <div className="mb-4" data-id="element-955">
          <h2 className="text-lg font-semibold" data-id="element-956">Active Goals</h2>
        </div>
        <div className="space-y-4" data-id="element-957">
          {goals.filter(g => g.status === 'inProgress').map(goal => <GoalCard key={goal.id} goal={goal} data-id="element-958" />)}
        </div>
        <button onClick={() => navigate('/goals')} className="w-full mt-4 text-sm text-purple-600 hover:text-purple-700 font-medium text-center" data-id="element-959">
          View Goals Hub
        </button>
      </div>
      <div className="bg-white rounded-xl shadow-sm p-6" data-id="element-960">
        <h2 className="text-lg font-semibold mb-4" data-id="element-961">Recommended Resources</h2>
        <div className="space-y-4" data-id="element-962">
          {dashboardData.recommendedResources.map(resource => <div key={resource.id} className="group" data-id="element-963">
              <div className="flex items-start space-x-3 cursor-pointer" onClick={() => setShowResourcePreview(resource.id)} data-id="element-964">
                <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0" data-id="element-965">
                  <EyeIcon className="text-gray-400 group-hover:text-purple-600" size={20} data-id="element-966" />
                </div>
                <div data-id="element-967">
                  <h3 className="font-medium text-sm group-hover:text-purple-600" data-id="element-968">
                    {resource.title}
                  </h3>
                  <p className="text-sm text-gray-600 line-clamp-2" data-id="element-969">
                    {resource.description}
                  </p>
                  <div className="flex items-center mt-1" data-id="element-970">
                    <img src={dashboardData.mentor.image} alt={dashboardData.mentor.name} className="w-4 h-4 rounded-full mr-2" data-id="element-971" />
                    <span className="text-xs text-gray-500" data-id="element-972">
                      Recommended by {dashboardData.mentor.name}
                    </span>
                  </div>
                </div>
              </div>
            </div>)}
        </div>
      </div>
      <div className="bg-white rounded-xl shadow-sm p-6" data-id="element-973">
        <div className="flex justify-between items-start mb-4" data-id="element-974">
          <div data-id="element-975">
            <h2 className="text-lg font-semibold mb-1" data-id="element-976">Your Journey</h2>
            <p className="text-sm text-gray-600" data-id="element-977">
              3 months into your mentorship
            </p>
          </div>
          <TrendingUpIcon className="text-purple-600" data-id="element-978" />
        </div>
        <div className="w-full bg-gray-100 rounded-full h-2 mb-2" data-id="element-979">
          <div className="bg-purple-600 h-2 rounded-full transition-all duration-300" style={{
          width: '30%'
        }} data-id="element-980" />
        </div>
        <div className="flex justify-between text-sm text-gray-600 mb-4" data-id="element-981">
          <span data-id="element-982">Start</span>
          <span data-id="element-983">Current: Month 3</span>
          <span data-id="element-984">Month 12</span>
        </div>
        <div className="flex items-center justify-end space-x-4 text-sm" data-id="element-985">
          <button onClick={() => setShowExtendModal(true)} className="cta-primary px-6" data-id="element-986">
            Extend Mentorship
          </button>
          <button onClick={() => navigate('/matching')} className="cta-primary px-6" data-id="element-987">
            Find Next Mentor
          </button>
        </div>
      </div>
      {showExtendModal && <ExtendMentorshipModal onClose={() => setShowExtendModal(false)} data-id="element-988" />}
      {showResourcePreview !== null && <ResourcePreviewModal resource={dashboardData.recommendedResources.find(r => r.id === showResourcePreview)} onClose={() => setShowResourcePreview(null)} data-id="element-989" />}
      {showTrophyDetails !== null && <TrophyPreviewModal trophy={trophies.find(t => t.id === showTrophyDetails)!} onClose={() => setShowTrophyDetails(null)} data-id="element-990" />}
      {showGoalDetailModal && <GoalDetailModal goal={showGoalDetailModal} onClose={() => setShowGoalDetailModal(null)} data-id="element-991" />}
    </div>;
};
export default MenteeDashboard;
const styles = `
.hide-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
.hide-scrollbar::-webkit-scrollbar {
  display: none;
}
`;